if (!customElements.get('collection-grid')) {
    const PRELOAD_LINK_DATA_ATTR = 'data-collection-grid-preload';

    const isUnmodifiedLeftClick = (event) =>
        event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

    const setControlDisabled = (el, isDisabled) => {
        if (!el) {
            return;
        }

        if (el instanceof HTMLButtonElement) {
            el.disabled = isDisabled;
        }
    };

    const preloadImage = (src, srcset, sizes) => {
        if (!src) {
            return;
        }

        const selector = `link[rel="preload"][as="image"][href="${CSS.escape(src)}"], link[${PRELOAD_LINK_DATA_ATTR}="${CSS.escape(src)}"]`;

        if (document.head.querySelector(selector)) {
            return;
        }

        const elLink = document.createElement('link');
        elLink.rel = 'preload';
        elLink.as = 'image';
        elLink.href = src;

        if (srcset) {
            elLink.setAttribute('imagesrcset', srcset);
        }

        if (sizes) {
            elLink.setAttribute('imagesizes', sizes);
        }

        elLink.setAttribute(PRELOAD_LINK_DATA_ATTR, src);
        document.head.appendChild(elLink);
    };

    class CollectionGrid extends HTMLElement {
        #observer = null;

        #prefetchObserver = null;

        #abortController = null;

        #prefetchAbortController = null;

        #loading = false;

        #prefetching = false;

        #pendingFragment = null;

        #pendingPage = null;

        #pendingPageTotal = null;

        #cnstrcNumResults = null;

        #onPopState = null;

        #onPaginationClick = null;

        connectedCallback() {
            this.sectionId = this.getAttribute('section-id');
            this.elProducts = this.querySelector('[data-products]');
            this.elSentinel = this.querySelector('[data-sentinel]');
            this.elLoadMore = this.querySelector('[data-load-more]');
            this.elLoader = this.querySelector('[data-loader]');
            this.elStatus = this.querySelector('[data-status]');
            this.elCount = this.querySelector('[data-count]');
            this.elPagination = this.querySelector('[data-pagination]');
            this.elPagePrev = this.querySelector('[data-page-prev]');
            this.elPageNext = this.querySelector('[data-page-next]');
            this.elPageButtons = this.querySelectorAll('[data-page]');

            this.currentPage = Number(this.getAttribute('page-current'));
            this.pageTotal = Number(this.getAttribute('page-total'));
            this.infiniteScroll = this.getAttribute('infinite-scroll') !== 'false';

            this.labelLoading = this.dataset.labelLoading || 'Loading more products';
            this.labelLoaded = this.dataset.labelLoaded || '%count% more products loaded';
            this.labelError = this.dataset.labelError || 'Unable to load more products. Try again.';
            this.labelEnd = this.dataset.labelEnd || 'All products loaded';
            this.labelLoadingPage = this.dataset.labelLoadingPage || 'Loading products';

            if (!this.sectionId || !this.elProducts) {
                return;
            }

            this.#updateFooterVisibility();

            this.#onPopState = () => {
                this.#syncFromUrl();
            };

            window.addEventListener('popstate', this.#onPopState);

            if (this.pageTotal <= 1) {
                return;
            }

            if (this.infiniteScroll) {
                this.#initInfiniteScroll();
                return;
            }

            this.#bindPaginationEvents();
        }

        disconnectedCallback() {
            this.#observer?.disconnect();
            this.#prefetchObserver?.disconnect();
            this.#abortController?.abort();
            this.#prefetchAbortController?.abort();

            if (this.#onPopState) {
                window.removeEventListener('popstate', this.#onPopState);
            }

            if (this.elPagination && this.#onPaginationClick) {
                this.elPagination.removeEventListener('click', this.#onPaginationClick);
                this.#onPaginationClick = null;
            }
        }

        applyFilters(form) {
            const url = new URL(form.action, window.location.origin);
            url.search = '';

            for (const [key, value] of new FormData(form).entries()) {
                if (value) {
                    url.searchParams.append(key, value);
                }
            }

            url.searchParams.delete('page');

            return this.applyFromUrl(this.#withSearchQuery(url).toString(), { source: 'filter' });
        }

        applyFromUrl(url, { updateHistory = true, source = 'filter' } = {}) {
            if (this.#loading) {
                return Promise.resolve();
            }

            const resolvedUrl = this.#withSearchQuery(url);
            const fetchUrl = this.#buildSectionUrl(resolvedUrl.toString());

            return this.#applySectionUpdate(fetchUrl, {
                updateHistory,
                historyUrl: this.#buildHistoryUrlFromUrl(resolvedUrl.toString()),
                source,
                fade: true,
                resetInfiniteScroll: true,
            });
        }

        #withSearchQuery(url) {
            const resolvedUrl = new URL(url, window.location.origin);
            const searchQuery =
                this.dataset.searchQuery || new URL(window.location.href).searchParams.get('q');

            if (searchQuery && !resolvedUrl.searchParams.get('q')) {
                resolvedUrl.searchParams.set('q', searchQuery);
            }

            return resolvedUrl;
        }

        #initInfiniteScroll() {
            this.elLoadMore?.addEventListener('click', (event) => {
                if (!isUnmodifiedLeftClick(event)) {
                    return;
                }

                event.preventDefault();
                this.#loadNextPage();
            });

            if (!this.elSentinel) {
                return;
            }

            this.#observer = new IntersectionObserver(
                (entries) => {
                    for (const entry of entries) {
                        if (entry.isIntersecting) {
                            this.#loadNextPage();
                        }
                    }
                },
                { rootMargin: '400px 0px' }
            );
            this.#observer.observe(this.elSentinel);

            this.#prefetchObserver = new IntersectionObserver(
                (entries) => {
                    for (const entry of entries) {
                        if (entry.isIntersecting) {
                            this.#prefetchNextPage();
                        }
                    }
                },
                { rootMargin: '1000px 0px' }
            );
            this.#prefetchObserver.observe(this.elSentinel);
        }

        #bindPaginationEvents() {
            if (!this.elPagination || this.#onPaginationClick) {
                return;
            }

            this.#onPaginationClick = (event) => {
                if (!isUnmodifiedLeftClick(event)) {
                    return;
                }

                const elTarget = event.target.closest('[data-page-prev], [data-page-next], [data-page]');

                if (!elTarget || !this.elPagination.contains(elTarget)) {
                    return;
                }

                event.preventDefault();

                if (elTarget.matches('[data-page-prev]')) {
                    this.#goToPage(this.currentPage - 1);
                    return;
                }

                if (elTarget.matches('[data-page-next]')) {
                    this.#goToPage(this.currentPage + 1);
                    return;
                }

                this.#goToPage(Number(elTarget.dataset.page));
            };

            this.elPagination.addEventListener('click', this.#onPaginationClick);
        }

        #syncFromUrl() {
            if (this.#loading) {
                return Promise.resolve();
            }

            const fetchUrl = this.#buildSectionUrl(window.location.href);

            return this.#applySectionUpdate(fetchUrl, {
                updateHistory: false,
                source: 'popstate',
                fade: true,
                resetInfiniteScroll: true,
            });
        }

        #applySectionUpdate(fetchUrl, {
            updateHistory = false,
            historyUrl = null,
            source = 'pagination',
            fade = true,
            resetInfiniteScroll = false,
        } = {}) {
            this.#clearConstructorResultCount();
            this.#setLoading(true);
            this.#announce(this.labelLoadingPage);

            if (fade) {
                this.elProducts.style.opacity = '0';
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }

            return this.#fetchSection(fetchUrl)
                .then((parsed) => {
                    const count = this.#renderSection(parsed, { resetInfiniteScroll });
                    this.elProducts.style.opacity = '1';

                    if (updateHistory && historyUrl) {
                        history.pushState({ collectionGridState: true }, '', historyUrl);
                    }

                    this.#setLoading(false);
                    this.#announce(this.labelLoaded.replace('%count%', String(count)));

                    this.dispatchEvent(
                        new CustomEvent('collection-grid:updated', {
                            bubbles: true,
                            detail: { elSection: parsed.elSection, source },
                        })
                    );
                })
                .catch((error) => {
                    if (error.name === 'AbortError') {
                        return;
                    }

                    this.elProducts.style.opacity = '1';
                    this.#restoreConstructorResultCount();
                    this.#setLoading(false);
                    this.#updatePaginationUI();
                    this.#announce(this.labelError);
                    console.error('collection-grid fetch failed', error);
                });
        }

        #renderSection(parsed, { resetInfiniteScroll = false } = {}) {
            const { elProducts, elPagination, elCount, pageCurrent, pageTotal, elGrid } = parsed;

            if (elGrid?.dataset.searchQuery) {
                this.dataset.searchQuery = elGrid.dataset.searchQuery;
            }

            this.elProducts.replaceChildren(...[...elProducts.children]);
            this.#replacePagination(elPagination);
            this.#replaceCount(elCount);

            this.currentPage = pageCurrent;
            this.setAttribute('page-current', String(pageCurrent));
            this.#syncConstructorResults(elGrid, pageCurrent);

            if (pageTotal) {
                this.pageTotal = pageTotal;
                this.setAttribute('page-total', String(pageTotal));
            }

            if (resetInfiniteScroll && this.infiniteScroll) {
                this.#resetInfiniteScrollState();
            }

            this.#updatePaginationUI();
            this.#updateFooterVisibility();
            this.#promoteViewportImages();
            window.initLazyLoad?.();

            return this.#countProductCards(this.elProducts);
        }

        #countProductCards(root) {
            return root.querySelectorAll('product-card').length;
        }

        #appendProductNodes(elSource) {
            for (const child of [...elSource.children]) {
                if (child.matches('[data-empty-state]')) {
                    continue;
                }

                const blockId = child.dataset.collectionGridBlock;

                if (blockId && this.elProducts.querySelector(`[data-collection-grid-block="${CSS.escape(blockId)}"]`)) {
                    continue;
                }

                this.elProducts.appendChild(child);
            }
        }

        #replacePagination(elPagination) {
            if (!this.elPagination) {
                return;
            }

            if (elPagination) {
                this.elPagination.innerHTML = elPagination.innerHTML;
                this.elPagination.hidden = false;
            } else {
                this.elPagination.hidden = true;
            }

            this.elPagePrev = this.querySelector('[data-page-prev]');
            this.elPageNext = this.querySelector('[data-page-next]');
            this.elPageButtons = this.querySelectorAll('[data-page]');
        }

        #replaceCount(elCount) {
            if (this.elCount && elCount) {
                this.elCount.textContent = elCount.textContent;
            }
        }

        #updateFooterVisibility() {
            const hasMultiplePages = this.pageTotal > 1;
            const hasProducts = Boolean(this.elProducts?.querySelector('product-card'));

            if (this.elPagination) {
                this.elPagination.hidden = !hasMultiplePages || !hasProducts;
            }

            const elFooter = this.querySelector('.collection-grid--footer');

            if (elFooter) {
                elFooter.hidden = !hasMultiplePages || !hasProducts;
            }
        }

        #resetInfiniteScrollState() {
            this.#clearPrefetch();
            this.#prefetchAbortController?.abort();

            if (this.elSentinel) {
                this.elSentinel.hidden = this.pageTotal <= 1;
            }

            if (this.elLoadMore) {
                this.elLoadMore.hidden = this.pageTotal <= 1;
                setControlDisabled(this.elLoadMore, false);
            }

            if (this.elLoader) {
                this.elLoader.hidden = true;
                this.elLoader.setAttribute('aria-hidden', 'true');
            }

            if (this.pageTotal <= 1) {
                this.#observer?.disconnect();
                this.#prefetchObserver?.disconnect();
                return;
            }

            if (this.currentPage >= this.pageTotal) {
                this.#setComplete();
                return;
            }

            if (this.elSentinel && !this.#observer) {
                this.#initInfiniteScroll();
            } else if (this.elSentinel && this.#observer) {
                this.#observer.observe(this.elSentinel);
                this.#prefetchObserver?.observe(this.elSentinel);
            }

            this.#prefetchNextPage();
        }

        #announce(message) {
            if (this.elStatus) {
                this.elStatus.textContent = message;
            }
        }

        #clearConstructorResultCount() {
            if (!this.hasAttribute('data-cnstrc-search') && !this.hasAttribute('data-cnstrc-browse')) return;
            if (!this.hasAttribute('data-cnstrc-num-results')) return;

            this.#cnstrcNumResults = this.getAttribute('data-cnstrc-num-results');
            this.removeAttribute('data-cnstrc-num-results');
        }

        #restoreConstructorResultCount() {
            if (this.#cnstrcNumResults == null) return;

            this.setAttribute('data-cnstrc-num-results', this.#cnstrcNumResults);
            this.#cnstrcNumResults = null;
        }

        #syncConstructorResults(elGrid, page) {
            if (this.hasAttribute('data-cnstrc-search') || this.hasAttribute('data-cnstrc-browse')) {
                const nextCount = elGrid?.getAttribute('data-cnstrc-num-results');

                if (nextCount != null) {
                    this.setAttribute('data-cnstrc-num-results', nextCount);
                    this.#cnstrcNumResults = null;
                }
            }

            const nextTerm = elGrid?.getAttribute('data-cnstrc-search-term');

            if (this.hasAttribute('data-cnstrc-search-term') && nextTerm != null) {
                this.setAttribute('data-cnstrc-search-term', nextTerm);
            }

            this.#syncConstructorResultPage(page);
        }

        #syncConstructorResultPage(page) {
            if (!this.hasAttribute('data-cnstrc-result-page') || page == null) return;

            this.setAttribute('data-cnstrc-result-page', String(page));
        }

        #setLoading(isLoading) {
            this.#loading = isLoading;
            this.classList.toggle('loading', isLoading);

            if (this.elLoader) {
                this.elLoader.hidden = !isLoading;
                this.elLoader.setAttribute('aria-hidden', String(!isLoading));
            }

            setControlDisabled(this.elLoadMore, isLoading);

            if (this.elPagePrev) {
                this.elPagePrev.hidden = this.currentPage <= 1;
                setControlDisabled(this.elPagePrev, isLoading);
            }

            if (this.elPageNext) {
                this.elPageNext.hidden = this.currentPage >= this.pageTotal;
                setControlDisabled(this.elPageNext, isLoading);
            }

            for (const elPageButton of this.elPageButtons) {
                setControlDisabled(elPageButton, isLoading);
            }
        }

        #setComplete() {
            this.#observer?.disconnect();
            this.#prefetchObserver?.disconnect();
            this.#clearPrefetch();

            if (this.elSentinel) {
                this.elSentinel.hidden = true;
            }

            if (this.elLoadMore) {
                this.elLoadMore.hidden = true;
            }

            if (this.elLoader) {
                this.elLoader.hidden = true;
                this.elLoader.setAttribute('aria-hidden', 'true');
            }

            this.#announce(this.labelEnd);
        }

        #clearPrefetch() {
            this.#pendingFragment = null;
            this.#pendingPage = null;
            this.#pendingPageTotal = null;
        }

        #buildSectionUrl(url) {
            const sectionUrl = new URL(url, window.location.origin);
            sectionUrl.searchParams.set('section_id', this.sectionId);
            return sectionUrl.toString();
        }

        #buildPageUrl(page) {
            const url = new URL(window.location.href);
            url.searchParams.set('page', String(page));
            return this.#buildSectionUrl(url.toString());
        }

        #buildHistoryUrlFromPage(page) {
            const url = new URL(window.location.href);

            if (page === 1) {
                url.searchParams.delete('page');
            } else {
                url.searchParams.set('page', String(page));
            }

            url.searchParams.delete('section_id');
            return url.toString();
        }

        #buildHistoryUrlFromUrl(url) {
            const historyUrl = new URL(url, window.location.origin);
            const page = Number(historyUrl.searchParams.get('page') || 1);

            if (page === 1) {
                historyUrl.searchParams.delete('page');
            }

            historyUrl.searchParams.delete('section_id');
            return historyUrl.toString();
        }

        #parseSectionResponse(responseText) {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const elSection = html.querySelector(`#shopify-section-${this.sectionId}`);
            const elProducts = elSection?.querySelector('[data-products]');
            const elGrid = elSection?.querySelector('collection-grid');
            const elPagination = elSection?.querySelector('[data-pagination]');
            const elCount = elSection?.querySelector('[data-count]');
            const elFiltersForm = elSection?.querySelector('.collection-filters--form');

            if (!elProducts) {
                throw new Error('No products container in response');
            }

            return {
                elSection,
                elProducts,
                elPagination,
                elCount,
                elFiltersForm,
                elGrid,
                pageCurrent: elGrid ? Number(elGrid.getAttribute('page-current')) : 1,
                pageTotal: elGrid ? Number(elGrid.getAttribute('page-total')) : this.pageTotal,
            };
        }

        #preloadPrimaryImages(root) {
            for (const elImg of root.querySelectorAll('[data-image] > img:not(.hover)')) {
                preloadImage(elImg.src, elImg.srcset || '', elImg.sizes || '');
            }
        }

        #promoteViewportImages() {
            for (const elImg of this.elProducts.querySelectorAll('[data-image] > img:not(.hover)')) {
                const { top, bottom } = elImg.getBoundingClientRect();

                if (top < window.innerHeight && bottom > 0) {
                    elImg.loading = 'eager';
                }
            }
        }

        #updatePaginationUI() {
            if (!this.elPagination) {
                return;
            }

            if (this.elPagePrev) {
                this.elPagePrev.hidden = this.currentPage <= 1;
                setControlDisabled(this.elPagePrev, this.#loading);
            }

            if (this.elPageNext) {
                this.elPageNext.hidden = this.currentPage >= this.pageTotal;
                setControlDisabled(this.elPageNext, this.#loading);
            }

            for (const elPageButton of this.elPageButtons) {
                const isCurrentPage = Number(elPageButton.dataset.page) === this.currentPage;

                if (isCurrentPage) {
                    elPageButton.setAttribute('aria-current', 'page');
                } else {
                    elPageButton.removeAttribute('aria-current');
                }
            }
        }

        #fetchSection(url) {
            this.#abortController?.abort();
            this.#abortController = new AbortController();

            return fetch(url, { signal: this.#abortController.signal })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}`);
                    }

                    return response.text();
                })
                .then((responseText) => {
                    const parsed = this.#parseSectionResponse(responseText);

                    this.#preloadPrimaryImages(parsed.elProducts);

                    return parsed;
                });
        }

        #fetchPage(page) {
            return this.#fetchSection(this.#buildPageUrl(page)).then((parsed) => ({
                elProducts: parsed.elProducts,
                elPagination: parsed.elPagination,
                pageTotal: parsed.pageTotal,
            }));
        }

        #finalizePage(nextPage, pageTotal, count) {
            this.currentPage = nextPage;
            this.setAttribute('page-current', String(nextPage));
            this.#syncConstructorResultPage(nextPage);

            if (pageTotal) {
                this.pageTotal = pageTotal;
                this.setAttribute('page-total', String(pageTotal));
            }

            this.#promoteViewportImages();
            window.initLazyLoad?.();

            this.#setLoading(false);
            this.#announce(this.labelLoaded.replace('%count%', String(count)));

            if (this.currentPage >= this.pageTotal) {
                this.#setComplete();
                return;
            }

            this.#prefetchNextPage();
        }

        #goToPage(page, { updateHistory = true } = {}) {
            if (
                this.#loading ||
                page < 1 ||
                page > this.pageTotal ||
                page === this.currentPage
            ) {
                return Promise.resolve();
            }

            const historyUrl = this.#buildHistoryUrlFromPage(page);

            return this.#applySectionUpdate(this.#buildPageUrl(page), {
                updateHistory,
                historyUrl,
                source: 'pagination',
                fade: true,
                resetInfiniteScroll: false,
            });
        }

        #prefetchNextPage() {
            const nextPage = this.currentPage + 1;

            if (this.#prefetching || this.#loading || this.currentPage >= this.pageTotal) {
                return;
            }

            if (this.#pendingPage === nextPage) {
                return;
            }

            this.#prefetching = true;
            this.#prefetchAbortController?.abort();
            this.#prefetchAbortController = new AbortController();

            fetch(this.#buildPageUrl(nextPage), { signal: this.#prefetchAbortController.signal })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}`);
                    }

                    return response.text();
                })
                .then((responseText) => {
                    const parsed = this.#parseSectionResponse(responseText);
                    const { elProducts, pageTotal } = parsed;

                    this.#preloadPrimaryImages(elProducts);

                    this.#pendingPage = nextPage;
                    this.#pendingPageTotal = pageTotal;
                    this.#pendingFragment = document.createDocumentFragment();

                    for (const child of [...elProducts.children]) {
                        if (child.matches('[data-empty-state]')) {
                            continue;
                        }

                        const blockId = child.dataset.collectionGridBlock;

                        if (blockId && this.elProducts.querySelector(`[data-collection-grid-block="${CSS.escape(blockId)}"]`)) {
                            continue;
                        }

                        this.#pendingFragment.appendChild(child);
                    }
                })
                .catch((error) => {
                    if (error.name === 'AbortError') {
                        return;
                    }

                    console.error('collection-grid prefetch failed', error);
                })
                .finally(() => {
                    this.#prefetching = false;
                });
        }

        #loadNextPage() {
            if (this.#loading || this.currentPage >= this.pageTotal) {
                return;
            }

            const nextPage = this.currentPage + 1;

            this.#setLoading(true);
            this.#announce(this.labelLoading);

            if (this.#pendingFragment && this.#pendingPage === nextPage) {
                this.#prefetchAbortController?.abort();

                const count = this.#countProductCards(this.#pendingFragment);
                const pageTotal = this.#pendingPageTotal;
                this.elProducts.appendChild(this.#pendingFragment);
                this.#clearPrefetch();
                this.#finalizePage(nextPage, pageTotal, count);
                return;
            }

            this.#fetchPage(nextPage)
                .then(({ elProducts, pageTotal }) => {
                    const count = this.#countProductCards(elProducts);

                    this.#appendProductNodes(elProducts);

                    this.#clearPrefetch();
                    this.#finalizePage(nextPage, pageTotal, count);
                })
                .catch((error) => {
                    if (error.name === 'AbortError') {
                        return;
                    }

                    this.#setLoading(false);
                    this.#announce(this.labelError);
                    console.error('collection-grid fetch failed', error);
                });
        }
    }

    customElements.define('collection-grid', CollectionGrid);
}
